/**
 * Отказ от блюда.
 *
 * Одно окно на весь путь: слева позиции чека с количеством, справа
 * причина. Официант видит всё сразу и не листает — в час пик листать
 * некогда, а промахнуться по строке легко.
 *
 * Поштучно: гость заказал четыре шашлыка, съел два и отказался
 * от остального. Снять всю строку было бы неправдой.
 *
 * Причина обязательна: снятое блюдо стоит денег заведению, и без
 * причины брак кухни, ошибка официанта и передумавший гость выглядят
 * одинаково.
 */
import { useEffect, useRef, useState } from 'react';
import {
  VOID_REASONS, formatMoney, type VoidReason, needsComment,
} from '@dastarhan/core';
import { useKeyboard } from './useKeyboard';

export type VoidLine = {
  itemId: string;
  name: string;
  qty: number;
  unitPrice: number;
  fired?: boolean;
};

export function VoidReasonDialog({ lines, preselect, onConfirm, onCancel }: {
  /** живые позиции чека — все сразу, без прокрутки */
  lines: VoidLine[];
  /** позиция, выбранная заранее (нажали по строке чека) */
  preselect?: string | null;
  onConfirm: (itemId: string, reason: VoidReason, comment: string, qty: number) => void;
  onCancel: () => void;
}) {
  const [pickedId, setPickedId] = useState<string | null>(
    preselect ?? (lines.length === 1 ? lines[0]!.itemId : null),
  );
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState<VoidReason | null>(null);
  const [comment, setComment] = useState('');
  const kb = useKeyboard(comment, setComment, 'text');

  const picked = lines.find((l) => l.itemId === pickedId) ?? null;
  const ready = picked !== null && reason !== null
    && (!needsComment(reason) || comment.trim().length >= 3);

  /*
   * Ловушка, стоившая Дыму шести «Горилл»: выбор причины ощущается
   * финалом, а финал — кнопка ниже. Два предохранителя: кнопка сама
   * въезжает в кадр, а уйти молча из незавершённого отказа нельзя
   */
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (reason) confirmRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [reason]);
  const guardedCancel = () => {
    if (picked && reason && !window.confirm(
      'Отказ не завершён — позиция ОСТАНЕТСЯ в чеке.\nВыйти без снятия?',
    )) return;
    onCancel();
  };

  const pick = (l: VoidLine) => {
    setPickedId(l.itemId);
    // По умолчанию снимаем всё: чаще отказываются от блюда целиком,
    // а поштучно — редкий случай, для которого есть счётчик
    setQty(l.qty);
  };

  return (
    <div className="dialog-backdrop" role="dialog" aria-modal="true" aria-label="Отказ от блюда">
      <div className="dialog void-sheet">
        <span className="eyebrow">Отказ от блюда</span>
        <h1>{picked ? `Убрать «${picked.name}»` : 'Что убираем?'}</h1>

        <div className="void-cols">
          {/* Позиции: все на виду. Кнопка «Убрать» у каждой строки —
              официант жмёт по названию, а не ищет нужное в длинном списке */}
          <div className="void-lines">
            {lines.map((l) => (
              <button key={l.itemId}
                className={`void-line ${pickedId === l.itemId ? 'on' : ''}`}
                onClick={() => pick(l)}>
                <b>{l.name}</b>
                <span className="vl-qty">{l.qty} шт</span>
                <span className="vl-sum">{formatMoney(l.qty * l.unitPrice)}</span>
                {l.fired && <i className="vl-fired">на кухне</i>}
              </button>
            ))}
          </div>

          <div className="void-right">
            {!picked && (
              <p className="hint">Выберите блюдо слева — покажу, сколько снять и за что.</p>
            )}

            {picked && (
              <>
                {picked.fired && (
                  <p className="void-warn">
                    Блюдо уже на кухне. Если его готовили — продукты потрачены,
                    и снятие попадёт в списание.
                  </p>
                )}

                {picked.qty > 1 && (
                  <div className="void-qty">
                    <span>Сколько убрать</span>
                    <div className="vq-row">
                      <button className="btn" disabled={qty <= 1}
                        onClick={() => setQty((v) => Math.max(1, v - 1))}>−</button>
                      <b>{qty}</b>
                      <button className="btn" disabled={qty >= picked.qty}
                        onClick={() => setQty((v) => Math.min(picked.qty, v + 1))}>+</button>
                      <button className="btn ghost" onClick={() => setQty(picked.qty)}>
                        Все {picked.qty}
                      </button>
                    </div>
                    {qty < picked.qty && <i>В чеке останется {picked.qty - qty} шт.</i>}
                  </div>
                )}

                <p className="hint">Почему убираем</p>
                <div className="void-list">
                  {VOID_REASONS.map((r) => (
                    <button key={r.key}
                      className={`void-opt ${reason === r.key ? 'on' : ''}`}
                      onClick={() => setReason(r.key)}>
                      <b>{r.label}</b>
                      <span>{r.hint}</span>
                    </button>
                  ))}
                </div>

                {reason && needsComment(reason) && (
                  <label className="void-comment">
                    {reason === 'QUALITY'
                      ? 'Что именно не так — повару важно знать'
                      : 'Опишите словами'}
                    <input {...kb.props} value={comment}
                      placeholder={reason === 'QUALITY' ? 'пережарено, холодное…' : ''}
                      onChange={(e) => setComment(e.target.value)} />
                    {kb.node}
                  </label>
                )}
              </>
            )}
          </div>
        </div>

        <div className="dialog-actions">
          <button className="btn ghost" onClick={guardedCancel}>Отмена</button>
          <button ref={confirmRef} className={`btn danger ${ready ? 'pulse' : ''}`}
            disabled={!ready}
            onClick={() => onConfirm(picked!.itemId, reason!, comment.trim(), qty)}>
            {!ready
              ? 'Убрать из чека'
              : qty < picked!.qty
                ? `Убрать ${qty} из ${picked!.qty} — подтвердить`
                : `Убрать «${picked!.name}» — подтвердить`}
          </button>
        </div>
      </div>
    </div>
  );
}
